import { TrendingUp, TrendingDown, AlertTriangle, ShieldAlert, CheckCircle2, XCircle } from 'lucide-react';

export default function ResearchSection() {
  return (
    <section className="section" id="research">
      <div className="wrap">
        <div className="sec-tag"><TrendingUp size={13} />Deep Research</div>
        <h2>Bull. Bear. Devil's Advocate.<br /><em>All three. Every day.</em></h2>
        <p className="section-lead">
          Each covered stock gets a structured analysis: the bull case, the bear case, a devil's advocate
          challenge, and a risk grid — all sourced and labeled.
        </p>

        <div className="stock-header-card">
          <div className="shc-left">
            <div>
              <div className="shc-sym">MU</div>
              <div className="shc-name">Micron Technology · Storage &amp; Memory</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div className="shc-price">$785.20</div>
            <div className="shc-chg"><TrendingUp size={14} />+1.44%</div>
          </div>
        </div>

        <div className="bull-bear">
          <div className="case-card">
            <div className="case-card-head bull">
              <TrendingUp size={16} />
              <span className="case-tag">Bull Case</span>
              <span className="case-headline" style={{ marginLeft: 'auto' }}>HBM super-cycle incoming</span>
            </div>
            <div className="case-body">
              <p>Micron's HBM3E yield improvements position it to capture 20–25% of the high-bandwidth memory market by Q3 2026, as AI chip demand accelerates beyond current supply.</p>
              <p><strong>DRAM pricing index up 3.2% week-over-week</strong>, driven by hyperscaler demand for inference workloads.</p>
              <div className="case-signals">
                <div className="signal-row"><CheckCircle2 size={14} style={{ color: 'var(--green)' }} /><span><strong>HBM3E ramp:</strong> SK Hynix earnings confirm industry-wide demand surge</span></div>
                <div className="signal-row"><CheckCircle2 size={14} style={{ color: 'var(--green)' }} /><span><strong>Pricing power:</strong> NAND contract prices up 8% QoQ</span></div>
                <div className="signal-row"><CheckCircle2 size={14} style={{ color: 'var(--green)' }} /><span><strong>Capex discipline:</strong> No new fab announcements vs. Samsung overbuild</span></div>
              </div>
            </div>
          </div>

          <div className="case-card">
            <div className="case-card-head bear">
              <TrendingDown size={16} />
              <span className="case-tag">Bear Case</span>
              <span className="case-headline" style={{ marginLeft: 'auto' }}>PC oversupply risk</span>
            </div>
            <div className="case-body">
              <p>Consumer DRAM channel inventory remains elevated at 9–10 weeks vs. the healthy 6-week target. PC demand recovery stalled in Q1, and commodity DRAM is showing signs of oversupply pressure.</p>
              <p><strong>Samsung's aggressive pricing</strong> on standard DDR5 is compressing margins for non-HBM product lines.</p>
              <div className="case-signals">
                <div className="signal-row"><XCircle size={14} style={{ color: 'var(--red)' }} /><span><strong>Channel inventory:</strong> 9–10 weeks, elevated vs. 6w target</span></div>
                <div className="signal-row"><XCircle size={14} style={{ color: 'var(--red)' }} /><span><strong>Samsung pricing:</strong> DDR5 spot prices down 12% since February</span></div>
              </div>
            </div>
          </div>
        </div>

        <div className="da-card">
          <div className="da-head">
            <AlertTriangle size={16} />
            <span className="da-head-tag">Devil's Advocate</span>
          </div>
          <div className="da-body">
            <div className="da-question">
              <blockquote>"What if the HBM narrative is already priced in?"</blockquote>
            </div>
            <div className="da-content">
              <p>MU has outperformed the SOX index by 34% YTD. At 8.2× forward EV/Sales, the multiple assumes HBM gross margins of 55%+ through 2027 — a scenario that requires <strong>zero supply response from Samsung or SK Hynix</strong>.</p>
              <p>History shows memory oligopolies rarely maintain pricing discipline through a full upcycle. The bull thesis depends on an AI demand floor that has not yet been stress-tested against a hyperscaler capex pause.</p>
            </div>
          </div>
        </div>

        <div className="risk-grid">
          {[
            { sev: 'rs-hi',  title: 'Samsung supply response',          desc: 'Ramp HBM3E capacity, breaking pricing discipline' },
            { sev: 'rs-hi',  title: 'Hyperscaler capex pause',           desc: 'Any slowdown in AI infrastructure spend hits HBM demand immediately' },
            { sev: 'rs-med', title: 'Export control expansion',          desc: 'New ASML restrictions could slow Micron\'s leading-edge ramp' },
            { sev: 'rs-lo',  title: 'PC demand recovery muted',          desc: 'Consumer DRAM remains soft; not a bull thesis driver but a drag' },
          ].map((r, i) => (
            <div key={i} className="risk-item">
              <div className={`risk-sev-dot ${r.sev}`} />
              <div>
                <div className="risk-title">{r.title}</div>
                <div className="risk-desc">{r.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
